/**
 * Custom Fox Tan Image Gallery with Carousel
 * Based on card-images.js pattern
 * Adds carousel functionality to native image gallery section
 */
if (!customElements.get('custom-foxtan-image-gallery')) {
  class CustomImageGallery extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        galleryWrapper: '.image-gallery-wrapper',
        galleryGrid: '.image-gallery-grid',
        nextEl: '.swiper-btn-next',
        prevEl: '.swiper-btn-prev',
      };
      
      this.classes = {
        grid: 'f-grid',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.galleryWrapper = this.querySelector(this.selectors.galleryWrapper);
      this.galleryGrid = this.querySelector(this.selectors.galleryGrid);

      this.enableCarousel = this.dataset.enableCarousel === 'true';
      this.randomise = this.dataset.randomise === 'true';
      this.items = parseInt(this.dataset.items);
      this.tabletItems = parseInt(this.dataset.tabletItems);

      this.sliderInstance = false;

      // Randomise images BEFORE any initialization
      if (this.randomise) {
        this.shuffleGalleryItems();
      }

      if (!this.enableCarousel) return;

      // CRITICAL: Only initialize carousel on desktop (768px+)
      // On mobile, use native swipe functionality
      this.initCarouselIfDesktop();
      
      // Re-check on window resize
      window.addEventListener('resize', () => {
        this.handleResize();
      });
    }

    initCarouselIfDesktop() {
      const isDesktop = window.innerWidth >= 768;
      
      if (isDesktop && !this.sliderInstance) {
        this.init();
      } else if (!isDesktop && this.sliderInstance) {
        this.destroyCarousel();
      }
    }

    handleResize() {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.initCarouselIfDesktop();
      }, 150);
    }

    init() {
      this.initCarousel();
    }

    /**
     * Shuffle gallery items using Fisher-Yates algorithm
     * Randomises the order of images on page load
     */
    shuffleGalleryItems() {
      const items = Array.from(this.galleryGrid.children);
      
      // Fisher-Yates shuffle algorithm
      for (let i = items.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [items[i], items[j]] = [items[j], items[i]];
      }
      
      // Reorder DOM elements
      items.forEach(item => this.galleryGrid.appendChild(item));
    }

    initCarousel() {
      const columnGap = FoxTheme.utils.getGridColumnGap(this.galleryGrid);

      // Carousel options - show exact number of columns + peek effect
      const sliderOptions = {
        slidesPerView: 1,
        spaceBetween: 0,
        navigation: {
          nextEl: this.section.querySelector(this.selectors.nextEl),
          prevEl: this.section.querySelector(this.selectors.prevEl),
        },
        breakpoints: {
          768: {
            slidesPerView: 4.5, // Fixed: Always show 4.5 images at tablet
            spaceBetween: columnGap.tablet,
          },
          1024: {
            slidesPerView: parseInt(this.items) + 0.5, // Desktop: Honor column setting + peek
            spaceBetween: columnGap.tabletLarge,
          },
          1280: {
            slidesPerView: parseInt(this.items) + 0.5, // Desktop: Honor column setting + peek
            spaceBetween: columnGap.desktop,
          },
        },
        loop: false,
        threshold: 2,
        grabCursor: true,
        allowTouchMove: true,
        watchSlidesProgress: true,
      };

      if (typeof this.sliderInstance !== 'object') {
        this.galleryWrapper.classList.add(this.classes.swiper);
        this.galleryGrid.classList.remove(this.classes.grid);
        this.galleryGrid.classList.add(this.classes.swiperWrapper);
        this.sliderInstance = new window.FoxTheme.Carousel(this.galleryWrapper, sliderOptions);
        this.sliderInstance.init();

        // Accessibility: Focus management
        const focusableElements = FoxTheme.a11y.getFocusableElements(this);
        focusableElements.forEach((element) => {
          element.addEventListener('focusin', () => {
            const slide = element.closest('.swiper-slide');
            if (slide) {
              this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
            }
          });
        });
      }

      // Shopify theme editor support
      if (Shopify.designMode && typeof this.sliderInstance === 'object') {
        document.addEventListener('shopify:block:select', (e) => {
          if (e.detail.sectionId != this.sectionId) return;
          let { target } = e;
          const index = Number(target.dataset.index);
          this.sliderInstance.slider.slideTo(index);
        });
      }
    }

    destroyCarousel() {
      this.galleryWrapper.classList.remove(this.classes.swiper);
      this.galleryGrid.classList.remove(this.classes.swiperWrapper);
      this.galleryGrid.classList.add(this.classes.grid);
      if (typeof this.sliderInstance === 'object') {
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }
    }
  }
  customElements.define('custom-foxtan-image-gallery', CustomImageGallery);
}
